/**
 * Hidratação da Proposta PME a partir do snapshot da cotação.
 *
 * A proposta reaproveita o mesmo `PolicyId` da cotação, então o
 * `GET /portal/v1/group-life/proposals/:policyId` devolve o MESMO
 * `GroupLifeQuoteDetails` da cotação (estipulante, plano, coberturas, vidas e
 * cálculo). Estes mappers convertem esse payload nas formas de exibição
 * consumidas pelos steps do wizard de proposta.
 */
import type {
  CalculationCoverageGroup,
  CalculationSummaryCard,
  StipulantAccordionEntry,
} from '@/features/quotes/components/capital-global/steps/step-calculation.types';
import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import { resolveAll } from '@/features/quotes/utils/group-life/coverage-resolver';
import { resolveGroupLifeMonthlyPremium } from '@/features/quotes/utils/group-life/monthly-premium';
import { resolveMonthlyCoverageBreakdown } from '@/features/quotes/utils/group-life/monthly-coverage-premium.util';
import { roundHalfUp } from '@/shared/utils/round-half-up.util';
import {
  firstMeaningfulValue,
  monthlyFromAnnual,
} from '@/features/quotes/utils/group-life/coverage-unit-premium.util';
import {
  formatCurrencyBRL,
  formatGroupLifeDocument,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import type {
  GroupLifeFrontCustomerAddress,
  GroupLifeFrontMember,
  GroupLifeFrontStipulator,
  GroupLifeFrontSubStipulator,
  GroupLifeQuoteCalculation,
  GroupLifeQuoteDetails,
} from '@/services/group-life';
import type { PmeDemonstrativeRow, PmeLifeRow } from '../proposal.mock';
import { resolvePmeProposalStatusLabel } from './pme-proposal-lock.util';

/** Valores do formulário de Estipulante/Subestipulante (passo Dados do Seguro). */
export type PmeStipulantValues = {
  cnpj: string;
  /** true → CAEPF (pessoa física); false → CNPJ. Dirige rótulo/máscara e Sexo/Data. */
  isCaepf: boolean;
  companyName: string;
  mainActivity: string;
  /** CAEPF (pessoa física): sexo e data de nascimento do titular. */
  gender: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
};

const EMPTY_STIPULANT_VALUES: PmeStipulantValues = {
  cnpj: '',
  isCaepf: false,
  companyName: '',
  mainActivity: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

/** Rótulo do tipo de contratação a partir do código do Estipulante. */
export function insuranceTypeLabel(type?: '1' | '3' | '4'): string {
  switch (type) {
    case '3':
      return 'Renovação';
    case '4':
      return 'Transferência';
    case '1':
    default:
      return 'Novo';
  }
}

/** Espalha o endereço vindo do snapshot nos campos do formulário. */
function addressValues(
  address?: GroupLifeFrontCustomerAddress
): Partial<PmeStipulantValues> {
  if (!address) return {};
  return {
    cep: address.zipCode ?? '',
    street: address.street ?? '',
    number: address.number ?? '',
    complement: address.complement ?? '',
    neighborhood: address.neighborhood ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
  };
}

/** Preenche o formulário do Estipulante master a partir de `data.stipulator`. */
export function mapStipulatorValues(
  stipulator?: GroupLifeFrontStipulator
): PmeStipulantValues {
  if (!stipulator) return EMPTY_STIPULANT_VALUES;
  return {
    ...EMPTY_STIPULANT_VALUES,
    ...addressValues(stipulator.address),
    cnpj: formatGroupLifeDocument(
      stipulator.registerNumber,
      stipulator.isCaepf
    ),
    isCaepf: !!stipulator.isCaepf,
    companyName: stipulator.responsibleName ?? '',
    mainActivity: stipulator.cnaeCode ?? '',
    gender: stipulator.genderCode ?? '',
    dateOfBirth: (stipulator.dateOfBirth ?? '').split('T')[0] ?? '',
    email: stipulator.email ?? '',
    phone: stipulator.phone ?? '',
  };
}

/** Preenche o formulário de um SUBESTIPULANTE (mesmo shape do estipulante). */
export function mapSubStipulatorValues(
  sub?: GroupLifeFrontSubStipulator
): PmeStipulantValues {
  if (!sub) return EMPTY_STIPULANT_VALUES;
  return {
    ...EMPTY_STIPULANT_VALUES,
    ...addressValues(sub.address),
    cnpj: formatGroupLifeDocument(sub.cnpj, !!sub.isCaepf),
    isCaepf: !!sub.isCaepf,
    companyName: sub.legalName ?? '',
    mainActivity: sub.cnaeCode ?? '',
    gender: sub.genderCode ?? '',
    dateOfBirth: (sub.dateOfBirth ?? '').split('T')[0] ?? '',
    email: sub.email ?? '',
    phone: sub.phone ?? '',
  };
}

// ─── Resumo ──────────────────────────────────────────────────────────────

export type PmePremiumStatusContext = {
  statusPortal?: string | number | null;
  hasSignaturesStarted?: boolean;
};

export type PmePremiumValues = {
  totalValue: string;
  liquidValue: string;
  iofValue: string;
  frequency: string;
  validity: string;
  status: string;
};

export function mapPremiumValues(
  calculation?: GroupLifeQuoteCalculation,
  /** Status da proposta — sem ele o rótulo fala só do cálculo. */
  statusContext?: PmePremiumStatusContext
): PmePremiumValues {
  // Prêmio MENSAL da fonte única (`calculation.monthly`, calculado pelo back):
  // o mesmo número do resumo da cotação e dos PDFs. "Prêmio Líquido" = total −
  // IOF (não o `netPremium` atuarial), de forma que Líquido + IOF = Total.
  const monthly = resolveGroupLifeMonthlyPremium(calculation);
  return {
    totalValue: formatCurrencyBRL(monthly?.total ?? 0),
    liquidValue: formatCurrencyBRL(monthly?.beforeIof ?? 0),
    iofValue: formatCurrencyBRL(monthly?.iof ?? 0),
    frequency: 'Mensal',
    validity: calculation?.validity || '—',
    status: resolvePmeProposalStatusLabel({
      ...statusContext,
      isCalculated: Boolean(calculation?.calculated),
    }),
  };
}

/** Card "Dados do Seguro" do resumo. `effectiveDate` vem do passo 1 (vigência). */
export function mapInsuranceCard(
  stipulator?: GroupLifeFrontStipulator,
  effectiveDate?: string
): CalculationSummaryCard {
  return {
    title: 'Dados do Seguro',
    items: [
      { label: 'Início da Vigência', value: effectiveDate?.trim() || '—' },
      {
        label: 'Tipo de Seguro',
        value: insuranceTypeLabel(stipulator?.insuranceType),
      },
    ],
  };
}

/** Card "Dados do Estipulante" do resumo. */
export function mapStipulantCard(
  stipulator?: GroupLifeFrontStipulator
): CalculationSummaryCard {
  const isCaepf = stipulator?.isCaepf;
  return {
    title: 'Dados do Estipulante',
    items: [
      {
        label: isCaepf ? 'CAEPF' : 'CNPJ',
        value: stipulator
          ? formatGroupLifeDocument(stipulator.registerNumber, !!isCaepf) || '—'
          : '—',
      },
      {
        label: isCaepf ? 'Responsável' : 'Razão Social',
        value: stipulator?.responsibleName || '—',
      },
      { label: 'E-mail', value: stipulator?.email || '—' },
    ],
  };
}

/** Converte as vidas (estipulante master + subestipulantes) em linhas da tabela. */
export function mapLives(details?: GroupLifeQuoteDetails): PmeLifeRow[] {
  if (!details) return [];
  const fromMembers = (
    members: GroupLifeFrontMember[] | undefined,
    prefix: string
  ) =>
    (members ?? []).map((m, index) => ({
      id: m.id ?? `${prefix}-${index}`,
      status: (m.awayFromWork ? 'inactive' : 'active') as PmeLifeRow['status'],
      insurableGroup: m.insurableGroup || '—',
      capital: m.capital || '—',
      sex: m.gender || '—',
      birthDate: m.dateOfBirth || '—',
      name: m.name || '—',
    }));

  const stipulatorLives = fromMembers(details.stipulatorMembers, 'stip');
  const subLives = (details.subStipulators ?? []).flatMap((sub, subIndex) =>
    fromMembers(sub.members, `sub-${subIndex}`)
  );
  return [...stipulatorLives, ...subLives];
}

/**
 * Demonstrativo MENSAL por cobertura (`resolveMonthlyCoverageBreakdown`): as
 * linhas somam o prêmio mensal do card. Antes exibia o `breakdown` ANUAL sem
 * dividir. `description` pode vir como o CÓDIGO da cobertura — resolvido para
 * nome amigável via schema.
 */
export function mapDemonstrativeRows(
  calculation?: GroupLifeQuoteCalculation,
  coverageNameByCode?: Map<string, string>
): PmeDemonstrativeRow[] {
  return resolveMonthlyCoverageBreakdown(calculation).map(row => ({
    description: coverageNameByCode?.get(row.description) ?? row.description,
    // "Valor" = prêmio ANTES do IOF (total − IOF), mesmo critério do card de
    // prêmio.
    value: formatCurrencyBRL(roundHalfUp(row.total - row.iof)),
    iof: formatCurrencyBRL(row.iof),
    total: formatCurrencyBRL(row.total),
  }));
}

/** Percentuais de comercialização (Corretagem/Agenciamento). "Agência"
 *  (`agencyPercent`) saiu da lista: é campo legado do front e nem existe no
 *  data dictionary do Policy PME. */
export type PmeCommercializationRow = { label: string; value: string };

export function mapCommercializationRows(
  details?: GroupLifeQuoteDetails
): PmeCommercializationRow[] {
  const c = details?.commercialization;
  // Percentual não preenchido vale zero — o back devolve string VAZIA quando a
  // taxa não foi informada (mesma regra do resumo da cotação).
  const pct = (v?: string) => `${v?.trim() || '0,00'} %`;
  const rows: PmeCommercializationRow[] = [
    { label: 'Corretagem', value: pct(c?.brokeragePercent) },
    { label: 'Agenciamento', value: pct(c?.agencingPercent) },
  ];
  if (c?.coBrokers?.length) {
    rows.push({
      label: 'Participação do corretor',
      value: pct(c.leaderSharePercent || '100,00'),
    });
    const coBrokers = c.coBrokers;
    coBrokers.forEach((coBroker, index) => {
      const label =
        coBrokers.length > 1 ? `Co-corretor ${index + 1}` : 'Co-corretor';
      rows.push({
        label,
        value: pct(coBroker.sharePercent),
      });
    });
  }
  return rows;
}

// ─── Plano ─────────────────────────────────────────────────────────────────

export type PmeResolvedCoverage = {
  code: string;
  name: string;
  group: string;
  category: string;
  enabled: boolean;
  /** Valor exibido — o capital do grupo ou, nas coberturas sem capital
   *  (assistências), o valor mensal de uma vida. */
  capital: string | null;
};

export type ResolvePlanCoveragesOptions = {
  /** Soma dos capitais das vidas; ausente → soma a partir do snapshot. */
  memberCapitalOverride?: number;
  /** Quantidade de vidas — multiplica a IS por vida dos auxílios. */
  livesCount?: number;
  /** Prêmio puro anual por vida (`coverageUnitPurePremium`), por código e nome. */
  unitPurePremiumByCoverage?: Map<string, number>;
  /**
   * Capital tarifado deste estipulante. Ausente: usa o do plano, que é o do
   * master. A lista passa o mapa de cada sub para não repetir a soma da apólice.
   */
  coverageSumInsured?: Record<string, number>;
};

/**
 * Resolve as coberturas do plano da cotação usando o schema do produto,
 * agrupadas por categoria oficial. Reaproveita o resolver do fluxo de cotação.
 * Só considera coberturas habilitadas (as efetivamente contratadas).
 */
export function resolvePlanCoverages(
  schema: CoverageSchemaResponse | undefined,
  details: GroupLifeQuoteDetails | undefined,
  {
    memberCapitalOverride,
    livesCount,
    unitPurePremiumByCoverage,
    coverageSumInsured,
  }: ResolvePlanCoveragesOptions = {}
): { category: string; items: PmeResolvedCoverage[] }[] {
  if (!schema || !details?.plan) return [];

  const selections = details.plan.coverages ?? {};
  const membersCapital =
    memberCapitalOverride ??
    mapLives(details).reduce(
      (acc, life) => acc + (parseCurrencyInput(life.capital) ?? 0),
      0
    );
  const uniform = parseCurrencyInput(details.plan.uniformCapital ?? '') ?? 0;
  const basis =
    details.plan.modality === 'UNIFORM' && uniform > 0
      ? Math.max(membersCapital, uniform)
      : membersCapital;
  const resolved = resolveAll(schema, selections, {
    totalMemberCapital: basis,
  });

  const order: string[] = [];
  const byCategory = new Map<string, PmeResolvedCoverage[]>();
  for (const def of schema.coverages) {
    const sel = selections[def.code];
    if (!sel?.enabled) continue;
    const category = def.category?.trim() || 'Outras coberturas';
    if (!byCategory.has(category)) {
      byCategory.set(category, []);
      order.push(category);
    }
    // Capital tarifado pelo InsureMO (soma das vidas, já com o teto de cada
    // cobertura e o limite individual do plano aplicados). A conta local só
    // entra quando ele não existe — cotação ainda não calculada —, e aí é uma
    // prévia: ela multiplica o percentual sem conhecer os tetos.
    const contratado = (
      coverageSumInsured ?? details.plan.coverageSumInsured
    )?.[def.code];
    const localCapital = resolved[def.code]?.resolvedCapital ?? null;
    // O tarifado não traz os auxílios em `coverageSumInsured`, e o resolver dá
    // a IS de UMA vida (`DefaultSI`). As demais linhas mostram o total do
    // grupo, então o auxílio também: IS × vidas — mesma conta das análises.
    const groupLocalCapital =
      def.sumInsuredDefinition === 'BENEFIT_SELECTION' && localCapital != null
        ? livesCount
          ? localCapital * livesCount
          : null
        : localCapital;
    const capital = firstMeaningfulValue(contratado, groupLocalCapital);
    const hasCapital = capital != null && capital > 0;
    // Assistência não tem capital (chega zero): o que ela tem é preço. Exibe o
    // VALOR MENSAL de uma vida — prêmio puro anual ÷ 12, o "Valor Mensal" das
    // análises. Sem tarifação ainda, fica sem valor: "R$ 0,00" dizia que era
    // de graça.
    const monthlyValue = hasCapital
      ? undefined
      : monthlyFromAnnual(
          firstMeaningfulValue(
            unitPurePremiumByCoverage?.get(def.code),
            unitPurePremiumByCoverage?.get(def.name)
          )
        );
    const value = hasCapital
      ? capital
      : monthlyValue != null && monthlyValue > 0
        ? monthlyValue
        : null;
    byCategory.get(category)!.push({
      code: def.code,
      name: def.name,
      group: def.group,
      category,
      enabled: true,
      capital: value != null ? formatCurrencyBRL(value) : null,
    });
  }

  return order.map(category => ({
    category,
    items: byCategory.get(category)!,
  }));
}

/** Nomes amigáveis por código de cobertura (para o demonstrativo). */
export function buildCoverageNameByCode(
  schema?: CoverageSchemaResponse
): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of schema?.coverages ?? []) map.set(c.code, c.name);
  return map;
}

/**
 * Accordions "Lista de Estipulantes" do resumo: estipulante master + cada
 * subestipulante. As coberturas contratadas são as mesmas; o capital é o de
 * cada grupo (`resolveGroups` recebe o mapa daquele estipulante).
 */
export function mapStipulantAccordions(
  details: GroupLifeQuoteDetails | undefined,
  resolveGroups: (
    coverageSumInsured?: Record<string, number>
  ) => { category: string; items: PmeResolvedCoverage[] }[]
): StipulantAccordionEntry[] {
  if (!details) return [];

  const coverageGroupsFor = (
    coverageSumInsured?: Record<string, number>
  ): CalculationCoverageGroup[] =>
    resolveGroups(coverageSumInsured).map(group => ({
      title: group.category,
      // Sem valor (assistência sem capital) a linha fica em branco: "—"
      // ainda parecia um valor no lugar do capital.
      items: group.items.map(item => ({
        name: item.name,
        capital: item.capital ?? '',
      })),
    }));

  const coverageGroups = coverageGroupsFor(details.plan?.coverageSumInsured);

  const entries: StipulantAccordionEntry[] = [];
  const stipulator = details.stipulator;
  entries.push({
    label: 'Estipulante',
    riskDataCard: {
      title: 'Dados do Risco',
      items: [
        {
          label: stipulator?.isCaepf ? 'CAEPF' : 'CNPJ',
          value: stipulator
            ? formatGroupLifeDocument(
                stipulator.registerNumber,
                !!stipulator.isCaepf
              ) || '—'
            : '—',
        },
        {
          label: stipulator?.isCaepf ? 'Responsável' : 'Razão Social',
          value: stipulator?.responsibleName || '—',
        },
      ],
    },
    coverageGroups,
    assistancesCapitalizationGroups: [],
  });

  (details.subStipulators ?? []).forEach((sub, index) => {
    entries.push({
      label: `Subestipulante ${index + 1}`,
      riskDataCard: {
        title: 'Dados do Risco',
        items: [
          {
            label: sub.isCaepf ? 'CAEPF' : 'CNPJ',
            value: formatGroupLifeDocument(sub.cnpj, !!sub.isCaepf) || '—',
          },
          { label: 'Razão Social', value: sub.legalName || '—' },
        ],
      },
      coverageGroups: coverageGroupsFor(
        sub.coverageSumInsured ?? details.plan?.coverageSumInsured
      ),
      assistancesCapitalizationGroups: [],
    });
  });

  return entries;
}
